/**
 * Authentication state management with Zustand
 */

import { create } from 'zustand';
import { User } from '@/types/api';
import * as authApi from '@/api/auth';
import { invalidateSwProbe, publishAuthToken } from '@/sw/mediaSw';

interface AuthState {
  // State
  token: string | null;
  user: User | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
  setError: (error: string | null) => void;
  clearError: () => void;
  isAuthenticated: () => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  token: localStorage.getItem('auth_token'),
  user: (() => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  })(),
  isLoading: false,
  error: null,

  // Login action
  login: async (username: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authApi.login({ username, password });

      set({
        token: response.access_token,
        isLoading: false,
      });

      // Hand the new bearer to the media service worker (plan §6.2). It cannot
      // read localStorage, so a login it never hears about leaves it holding
      // the previous user's token — or none at all.
      invalidateSwProbe();
      void publishAuthToken(response.access_token);

      // Load user profile after successful login
      await get().loadUser();
    } catch (error: any) {
      let errorMessage = 'Login failed';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map((e: any) => e.msg || e).join(', ');
      }
      set({
        isLoading: false,
        error: errorMessage,
        token: null,
        user: null,
      });
      throw error;
    }
  },

  // Logout action
  logout: () => {
    authApi.logout();
    // Clears BOTH the worker's module variable and its IndexedDB mirror, so a
    // worker revived after sign-out cannot authenticate with the old token.
    void publishAuthToken(null);
    set({
      token: null,
      user: null,
      error: null,
    });
  },

  // Load user profile
  loadUser: async () => {
    set({ isLoading: true, error: null });

    try {
      const user = await authApi.getCurrentUser();
      set({
        user,
        isLoading: false,
      });
    } catch (error: any) {
      let errorMessage = 'Failed to load user';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map((e: any) => e.msg || e).join(', ');
      }
      set({
        isLoading: false,
        error: errorMessage,
        user: null,
      });
      throw error;
    }
  },

  // Set error
  setError: (error: string | null) => {
    set({ error });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const state = get();
    return !!state.token && !!state.user;
  },
}));

export default useAuthStore;
